/**
 * Google Ads / warehouse exports often store currency in micro-units (1e-6 of account currency).
 * Very large raw values are converted; smaller values are left as-is (already in currency).
 */
const MICROS = 1_000_000;
/** Values this large are effectively always micros for single-entity rows. */
const MICROS_LIKELY_THRESHOLD = 10_000_000;

export type GoogleAdsMoneyNormalizeResult = {
  /** Spend or budget in account currency for display. */
  amount: number;
  /** Used with {@link adjustGoogleAdsApiRoas} when spend was converted from micro-units. */
  roasScale: number;
};

export function normalizeGoogleAdsMoneyValue(raw: unknown): GoogleAdsMoneyNormalizeResult {
  const n = Number(raw);
  if (!Number.isFinite(n)) {
    return { amount: 0, roasScale: 1 };
  }
  if (Math.abs(n) >= MICROS_LIKELY_THRESHOLD) {
    return { amount: n / MICROS, roasScale: MICROS };
  }
  return { amount: n, roasScale: 1 };
}

/**
 * When spend/budget were converted from micros, API ROAS is sometimes still scaled as if
 * revenue was in currency while spend was in micros (tiny values like 1e-6). Scale ROAS
 * only in that suspicious range so correct values (e.g. 14x) are untouched.
 */
export function adjustGoogleAdsApiRoas(apiRoas: unknown, roasScale: number): number {
  const r = Number(apiRoas);
  if (!Number.isFinite(r)) {
    return 0;
  }
  if (roasScale > 1 && r > 0 && r < 0.01) {
    return r * roasScale;
  }
  return r;
}
